import BB20Service from './BB20Service'
import FetchWrapper from './FetchWrapper'
import { FilterRequest } from '../models/FilterRequest'
import { PagedResponse } from '../models/PagedResponse'
import { convertToFormattedDate } from '../views/common'

import { Label, Order, OrderWithOrderData } from '../models/Order'
import { AddOrderEntries } from '../models/AddOrderEntries'
import { LoadingUnit } from '../models/Loadingunit'
import { SliceResponse } from '@/models/SliceResponse'
import { Bulk, BulkDeleteErrors, BulkDocument, BulkValidationResponse, TransportMode } from '../models/Bulk'

import { BulkField } from '@/models/BulkField'

class CoreService extends BB20Service {
  frontEndSupportUrl: string
  manifestUploadServiceUrl: string

  constructor() {
    super(process.env.REACT_APP_CORE_SERVICE_URL!)
    this.frontEndSupportUrl = process.env.REACT_APP_CORE_FRONTEND_SUPPORT_URL!
    this.manifestUploadServiceUrl = process.env.REACT_APP_MANIFEST_UPLOAD_URL!
  }

  getJavaMelodyUrl(): string {
    return `${this.serviceUrl}/monitoring`
  }

  getSwaggerUrl(): string {
    return `${this.serviceUrl}/swagger-ui.html`
  }

  // ################## Order Service ################## //
  getAllOrdersFiltered(payload: FilterRequest): Promise<SliceResponse<Order>> {
    return FetchWrapper.POST(`${this.frontEndSupportUrl}/search/order`, {
      contentType: 'application/json',
      body: JSON.stringify(payload)
    })
  }

  getOrderCount(payload: FilterRequest) {
    return FetchWrapper.POST(`${this.frontEndSupportUrl}/search/order/count`, {
      contentType: 'application/json',
      body: JSON.stringify(payload)
    })
  }

  getAddOrderEntries(
    payload: FilterRequest
  ): Promise<PagedResponse<AddOrderEntries>> {
    return FetchWrapper.POST(
      `${this.frontEndSupportUrl}/views/add-orders-to-loading-unit`,
      {
        contentType: 'application/json',
        body: JSON.stringify(payload)
      }
    )
  }

  getAllLabelsFiltered(payload: FilterRequest): Promise<SliceResponse<Label>> {
    return FetchWrapper.POST(`${this.frontEndSupportUrl}/search/barcode`, {
      contentType: 'application/json',
      body: JSON.stringify(payload)
    })
  }

  getBarcodeCount(payload: FilterRequest) {
    return FetchWrapper.POST(`${this.frontEndSupportUrl}/search/barcode/count`, {
      contentType: 'application/json',
      body: JSON.stringify(payload)
    })
  }

  getAllMrnNames(): Promise<string[]> {
    return FetchWrapper.GET(`${this.frontEndSupportUrl}/search/mrnNames`, {})
  }

  getAllMRNsFiltered(payload: FilterRequest): Promise<any> {
    return FetchWrapper.POST(`${this.frontEndSupportUrl}/search/mrn`, {
      contentType: 'application/json',
      body: JSON.stringify(payload)
    })
  }

  async getOrderWithOrderData(orderId: string): Promise<OrderWithOrderData> {
    return FetchWrapper.GET(`${this.serviceUrl}/orders/${orderId}/frontend-details`)
  }

  async patchOrder(payload: any): Promise<Order> {
    return FetchWrapper.PATCH(`${this.frontEndSupportUrl}/${payload.orderId}`, {
      contentType: 'application/json',
      body: JSON.stringify(payload)
    })
  }

  // ################## Loading unit service ################## //
  async createLoadingUnit(
    loadingUnitWarehouseCode: string,
    destination?: string,
    lmc?: string,
    amount: string = '1'
  ): Promise<LoadingUnit[]> {
    if (loadingUnitWarehouseCode) {
      const payload = {
        warehouseCode: loadingUnitWarehouseCode,
        destination: destination && destination.trim() !== '' ? { destinationCode: destination } : null,
        lmc: lmc
      }

      return FetchWrapper.POST(
        `${this.serviceUrl}/loading-units?` + new URLSearchParams({ amount: amount }),
        {
          contentType: 'application/json',
          body: JSON.stringify(payload)
        }
      )
    }
    return FetchWrapper.POST(`${this.serviceUrl}/loading-units`)
  }

  getAllFilteredLoadingUnit(payload: FilterRequest): Promise<PagedResponse<LoadingUnit>> {
    return FetchWrapper.POST(`${this.serviceUrl}/loading-units/search`, {
      contentType: 'application/json',
      body: JSON.stringify(payload)
    })
  }

  getAllExportMrn(): Promise<PagedResponse<LoadingUnit>> {
    return FetchWrapper.GET(`${this.serviceUrl}/loading-units/distinct-export-mrn`)
  }

  async getLoadingUnitById(loadingUnitId: string): Promise<LoadingUnit> {
    return FetchWrapper.GET(`${this.serviceUrl}/loading-units/${loadingUnitId}`)
  }

  async getAllLoadingUnitsByBulkId(bulkId: string): Promise<LoadingUnit[]> {
    const url = `${this.serviceUrl}/loading-units?` + new URLSearchParams({ bulkId: bulkId })
    return FetchWrapper.GET(url)
  }

  async addOrders(
    loadingUnitId: string,
    orderBarcodes: string[]
  ): Promise<LoadingUnit> {
    const url = `${this.serviceUrl}/loading-units/${loadingUnitId}/orders`
    return FetchWrapper.PUT(url, {
      contentType: 'application/json',
      body: JSON.stringify(orderBarcodes)
    })
  }

  async removeOrders(
    loadingUnitId: string,
    orderBarcodes: any[]
  ): Promise<LoadingUnit> {
    const url = `${this.serviceUrl}/loading-units/${loadingUnitId}/orders`
    return FetchWrapper.DELETE(url, {
      contentType: 'application/json',
      body: JSON.stringify(orderBarcodes)
    })
  }

  deleteLoadingUnit(loadingUnitId: string): Promise<void> {
    const url = `${this.serviceUrl}/loading-units/${loadingUnitId}`
    return FetchWrapper.DELETE(url, { contentType: 'application/json' })
  }

  downloadFilterResult(filterRequest: any): Promise<any> {
    return FetchWrapper.POST(`${this.serviceUrl}/loading-units/filter-export`, {
      contentType: 'application/json',
      body: JSON.stringify(filterRequest),
      filename:
        'loading_unit_overview_' +
        convertToFormattedDate(
          new Date().toISOString(),
          undefined,
          'yyyy-MM-dd HH_mm_ss'
        ).toString() +
        '.xlsx'
    })
  }

  generateLoadingUnitLabelPDF(loadingUnitIds: string[], filename: string): Promise<void> {
    const url = `${this.serviceUrl}/loading-units/generate-pdf`
    return FetchWrapper.POST(url, {
      contentType: 'application/json',
      filename: filename,
      body: JSON.stringify(loadingUnitIds)
    })
  }

// ################## Bulk Service ################## //
  createBulk(warehouseCode: string, laneType: string): Promise<Bulk> {
    const body = JSON.stringify({ warehouseCode, laneType })
    return FetchWrapper.POST(`${this.serviceUrl}/bulks`, {
      contentType: 'application/json',
      body: body
    })
  }

  getAllFilteredBulk(payload: FilterRequest): Promise<PagedResponse<Bulk>> {
    return FetchWrapper.POST(`${this.serviceUrl}/bulks/search`, {
      contentType: 'application/json',
      body: JSON.stringify(payload)
    })
  }

  getBulkRequiredFields(
    bulkId: string,
    transportMode: TransportMode,
    laneType: string
  ): Promise<BulkField[]> {
    return FetchWrapper.GET(`${this.serviceUrl}/frontend-support/bulks/required-fields?bulkId=${bulkId}&transportMode=${transportMode}&laneType=${laneType}`)
  }

  async finalizeBulk(bulk: Bulk): Promise<Bulk> {
    await this.updateBulk(bulk)
    //@ts-ignore
    return FetchWrapper.PUT(
      `${this.serviceUrl}/bulks/${bulk.bulkId}/status/FINALIZED`
    )
  }

  getBulkById(bulkId: string): Promise<Bulk> {
    return FetchWrapper.GET(`${this.serviceUrl}/bulks/${bulkId}`)
  }

  getStatusByBulkId(bulkId: string): Promise<string> {
    return FetchWrapper.GET(`${this.serviceUrl}/bulks/${bulkId}/status`)
  }

  addLoadingUnits(bulkId: string, loadingUnitIds: string[]): Promise<Bulk> {
    return FetchWrapper.PUT(
      `${this.serviceUrl}/bulks/${bulkId}/loading-units/${loadingUnitIds.join(',')}`
    )
  }

  removeLoadingUnits(bulkId?: string, loadingUnitId?: string): Promise<Bulk> {
    return FetchWrapper.DELETE(
      `${this.serviceUrl}/bulks/${bulkId}/loading-units/${loadingUnitId}`
    )
  }

  updateBulk(bulk: Bulk): Promise<Bulk> {
    return FetchWrapper.PATCH(`${this.serviceUrl}/bulks/${bulk.bulkId}`, {
      contentType: 'application/json',
      body: JSON.stringify(bulk)
    })
  }

  uploadFile(bulk: Bulk, file: any, fileType: string): Promise<BulkDocument> {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('fileType', fileType)
    return FetchWrapper.POST(`${this.serviceUrl}/bulks/${bulk.bulkId}/documents`, {
      body: formData
    })
  }

  uploadManifest(file: any): Promise<Bulk[]> {
    const formData = new FormData()
    formData.append('file', file)
    return FetchWrapper.POST(this.manifestUploadServiceUrl, {
      body: formData
    })
  }

  downloadTransportDocument(
    bulkId: string,
    bulkDocumentID: number,
    filename: string
  ): Promise<any> {
    return FetchWrapper.GET(
      `${this.serviceUrl}/bulks/${bulkId}/documents/${bulkDocumentID}`,
      { filename: filename }
    )
  }

  deleteTransportDocument(
    bulkId: string,
    bulkDocumentID: number
  ): Promise<any> {
    return FetchWrapper.DELETE(
      `${this.serviceUrl}/bulks/${bulkId}/documents/${bulkDocumentID}`
    )
  }

  deleteBulk(bulkId: string): Promise<BulkDeleteErrors> {
    const url = `${this.serviceUrl}/bulks/${bulkId}`
    return FetchWrapper.DELETE(url, { contentType: 'application/json' })
  }

  downloadBulkFilterResult(filterRequest: any): Promise<any> {
    return FetchWrapper.POST(`${this.serviceUrl}/bulks/filter-export`, {
      contentType: 'application/json',
      body: JSON.stringify(filterRequest),
      filename:
        'bulk_overview_' +
        convertToFormattedDate(
          new Date().toISOString(),
          undefined,
          'yyyy-MM-dd HH_mm_ss'
        ).toString() +
        '.xlsx'
    })
  }

  reOpenBulk(bulkId: string): Promise<Bulk> {
    const url = `${this.serviceUrl}/bulks/${bulkId}/status/REOPENED`
    return FetchWrapper.PUT(url, { contentType: 'application/json' })
  }

  getBulkValidationResult(bulk: Bulk): Promise<BulkValidationResponse[]> {
    return FetchWrapper.POST(`${this.serviceUrl}/bulks/validate`, {
      contentType: 'application/json',
      body: JSON.stringify(bulk)
    })
  }
}

export default CoreService
